import moment from 'moment';
import startCase from 'lodash/startCase';
import get from 'lodash/get';
import React from 'react';
import { connect } from 'react-redux';
import {
  Paper,

  Table,
  TableBody,
  TableHead,
  TableFooter,
  TableRow,
  TableCell,

  Typography,

  withStyles,
} from '@material-ui/core';
import DefaultResourceIcon from '@material-ui/icons/ListAlt';
import { ListButton } from 'ra-ui-materialui';
import {
  Title,
  List as ReactAdminList,
  Datagrid,
  NumberField,
  ReferenceField,
  TextField,
  ShowButton,
} from 'react-admin';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';

import ResponsiveContainer from 'recharts/lib/component/ResponsiveContainer';
import LineChart from 'recharts/lib/chart/LineChart';
import Line from 'recharts/lib/cartesian/Line';
import XAxis from 'recharts/lib/cartesian/XAxis';
import YAxis from 'recharts/lib/cartesian/YAxis';
import Tooltip from 'recharts/lib/component/Tooltip';
import Legend from 'recharts/lib/component/Legend';

import { getAdminLevel } from '../authProvider';
import RelativeDateField from './RelativeDateField';
import { Icon as AccountIcon } from './Account';
import { Icon as OfferOrderIcon } from './OfferOrder';
import { Icon as UserIcon } from './User';


const nameToIcon = {
  Account: AccountIcon,
  OfferOrder: OfferOrderIcon,
  user: UserIcon, // Backend returns 'user' instead of 'User'
};


const formatTotal = v => (Number.isFinite(v)
  ? v.toLocaleString('en-US', { useGrouping: true })
  : ''
);

const formatCurrency = v => (Number.isFinite(v)
  ? v.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
  : ''
);

const styles = {
  section: {
    marginTop: '2em',
  },
};


const RecentResourceList = withRouter(({
  resourceName,
  resource,
  children,
  match,
  location,
  className,
}) => (
  <div className={className}>
    <Typography variant="display1" gutterBottom>
      {`Recently Created ${resourceName}`}
      <ListButton to={`/${resource}`} label="View all" />
    </Typography>
    <ReactAdminList
      title=" "
      hasCreate
      hasEdit
      hasList
      hasShow
      resource={resource}
      actions={null}
      bulkActionButtons={false}
      match={match}
      location={location}
      perPage={5}
      basePath={`/${resource}`}
      sort={{
        field: 'createdAt',
        order: 'DESC',
      }}
    >
      {children}
    </ReactAdminList>
  </div>
));


const COLORS = [
  '#e41a1c',
  '#377eb8',
  '#4daf4a',
  '#984ea3',
  '#ff7f00',
  '#ffff33',
  '#a65628',
  '#f781bf',
  '#999999',
];


class Dashboard extends React.Component {
  componentDidMount() {
    this.refreshData();
  }
  componentDidUpdate(prevProps) {
    const {
      viewVersion,
    } = this.props;
    if (viewVersion > prevProps.viewVersion) {
      // Refresh button was clicked
      this.refreshData();
    }
  }

  refreshData = () => {
    if (getAdminLevel()) {
      this.props.fetchDashboardStatistics();
      this.props.fetchDashboardChartData();
    }
  };

  render() {
    if (!getAdminLevel()) {
      // Dashboard component may be mounted when user is not logged in.
      // The full Dashboard component in'ts rendered because the mounting
      // the children nodes (e.g. RecentResourceList) will do API calls that
      // will fail with 401 errors
      return null;
    }
    const {
      classes,
      statistics,
      chartData,
      countAllOfferOrders,
      sumAllOfferOrders,
    } = this.props;
    return (
      <React.Fragment>
        <Title title="Dashboard" />
        <Typography variant="display2" gutterBottom>
          My IPO
        </Typography>

        <div className={classes.section}>
          <Typography variant="display1" gutterBottom>
            Total Accounts and Order Offers
          </Typography>
          <ResponsiveContainer width="99%" height={300}>
            <LineChart data={chartData}>
              <Line type="monotone" dataKey="Account" dot={false} stroke={COLORS[0]} />
              <Line type="monotone" dataKey="OfferOrder" dot={false} stroke={COLORS[1]} />
              <XAxis dataKey="date" tickFormatter={v => moment(v).format('MM-DD-YYYY')} />
              <YAxis />
              <Tooltip />
              <Legend />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className={classes.section}>
          <Typography variant="display1" gutterBottom>
            Summary
          </Typography>

          <Typography variant="title" gutterBottom>
            Totals
          </Typography>
          <Paper style={{ marginBottom: '1em' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Resource</TableCell>
                  <TableCell numeric>Last 1 day</TableCell>
                  <TableCell numeric>Last 7 days</TableCell>
                  <TableCell numeric>Last 30 days</TableCell>
                  <TableCell numeric>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statistics && statistics.models && statistics.models.map(({ name, totals }) => {
                  const ResourceIcon = nameToIcon[name] || DefaultResourceIcon;
                  return (
                    <TableRow key={name}>
                      <TableCell><ResourceIcon /> {`${startCase(name)}s`}</TableCell>
                      <TableCell numeric>{formatTotal(totals.days1)}</TableCell>
                      <TableCell numeric>{formatTotal(totals.days7)}</TableCell>
                      <TableCell numeric>{formatTotal(totals.days30)}</TableCell>
                      <TableCell numeric>{formatTotal(totals.allTime)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>

          <Typography variant="title" gutterBottom>
            Orders
          </Typography>
          <Paper style={{ marginBottom: '1em' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Offer</TableCell>
                  <TableCell numeric>Count</TableCell>
                  <TableCell numeric>Sum of Request Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statistics && statistics.offerOrders && statistics.offerOrders.map(({
                  offer: { id, name },
                  count,
                  sum_requestedTotalCurrencyAmt, // eslint-disable-line camelcase
                }) => (
                  <TableRow key={name}>
                    <TableCell><Link style={{ color: 'inherit' }} to={`/offerings/${id}/show`}>{name}</Link></TableCell>
                    <TableCell numeric>{formatTotal(count)}</TableCell>
                    <TableCell numeric>{formatCurrency(sum_requestedTotalCurrencyAmt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell>Total</TableCell>
                  <TableCell numeric>{formatTotal(countAllOfferOrders)}</TableCell>
                  <TableCell numeric>{formatCurrency(sumAllOfferOrders)}</TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </Paper>
        </div>

        <RecentResourceList
          resourceName="Offer Orders"
          resource="offeringOrders"
          className={classes.section}
        >
          <Datagrid>
            <RelativeDateField label="Created" source="createdAt" sortable={false} />
            <ReferenceField label="Offer" source="offerId" reference="offerings" linkType={false} sortable={false}>
              <TextField source="name" />
            </ReferenceField>
            <NumberField
              label="Requested Amount"
              source="requestedTotalCurrencyAmt"
              sortable={false}
              options={{ style: 'currency', currency: 'USD' }}
            />
            <ShowButton />
          </Datagrid>
        </RecentResourceList>
      </React.Fragment>
    );
  }
}


const mapStateToProps = (state) => {
  const orders = get(state.dashboard.statistics, 'offerOrders', []);
  return {
    viewVersion: state.admin.ui.viewVersion,
    statistics: state.dashboard.statistics,
    chartData: state.dashboard.chartData,
    countAllOfferOrders: orders.reduce((acc, order) => acc + order.count, 0),
    sumAllOfferOrders: orders.reduce((acc, order) => acc + order.sum_requestedTotalCurrencyAmt, 0),
  };
};


const mapDispatchToProps = {
  fetchDashboardChartData: () => ({
    type: 'DASHBOARD_CHART_DATA',
    meta: {
      fetch: 'DASHBOARD_CHART_DATA',
    },
  }),
  fetchDashboardStatistics: () => ({
    type: 'DASHBOARD_STATISTICS',
    meta: {
      fetch: 'DASHBOARD_STATISTICS',
    },
  }),
};


export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(Dashboard));



// WEBPACK FOOTER //
// ./src/components/Dashboard.js